import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import { FaEye, FaTrash, FaUpload, FaEdit } from 'react-icons/fa';

import { useAddService } from './useAddService';
import {
  SERVICE_TYPE_DS,
  REFERRAL_LIST,
  SUB_SERVICE_LIST,
  TRAUMA_LABELS,
  TRAUMA_COLORS,
} from './enquiryConstants';

const BILLING_LOCKED = 3;

const SelectField = ({ id, name, label, value, onChange, disabled, options, optionKey, optionLabel, required, placeholder }) => (
  <div>
    <label htmlFor={id}>
      {label}{required && <span className="text-red-500">*</span>}
    </label>
    <select
      id={id}
      name={name}
      className="border border-gray-300 rounded-md w-full h-[40px] focus:border-3 border-[#6366f13d]"
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      <option value="">{placeholder || `Select ${label}`}</option>
      {options.map((opt) => (
        <option key={opt[optionKey]} value={opt[optionKey]}>{opt[optionLabel]}</option>
      ))}
    </select>
  </div>
);

SelectField.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  optionKey: PropTypes.string.isRequired,
  optionLabel: PropTypes.string.isRequired,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
};

SelectField.defaultProps = {
  value: '',
  disabled: false,
  required: false,
  placeholder: '',
};

const ReadonlyInput = ({ label, value, required }) => {
  const inputId = `readonly-${label.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <div>
      <label htmlFor={inputId}>{label}{required && <span className="text-red-500">*</span>}</label>
      <InputText id={inputId} disabled value={value || ''} className="border border-gray-300 rounded-md p-2 w-full" />
    </div>
  );
};

ReadonlyInput.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  required: PropTypes.bool,
};

ReadonlyInput.defaultProps = {
  value: '',
  required: false,
};

const NumberInput = ({ label, name, value, onChange, disabled, required }) => (
  <div>
    <label htmlFor={name}>{label}{required && <span className="text-red-500">*</span>}</label>
    <InputText
      id={name}
      name={name}
      keyfilter="num"
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      className="border border-gray-300 rounded-md p-2 w-full"
    />
  </div>
);

NumberInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
};

NumberInput.defaultProps = {
  value: '',
  disabled: false,
  required: false,
};

const TraumaDisplay = ({ traumaStatus, traumaReason }) => {
  const getLabel = (status) => TRAUMA_LABELS[status] || { main: '', sub: '' };
  const getColor = (status) => TRAUMA_COLORS[status] || 'black';

  if (traumaStatus === undefined) return null;
  const trauma = getLabel(traumaStatus);
  const color = getColor(traumaStatus);

  return (
    <div className="grid grid-cols-3 gap-4 mt-16 ml-10">
      <div className="col-span-3">
        <div>
          <span className="text-[#004172] font-bold">Trauma :</span>
          <span style={{ color: 'black' }}>{trauma.main}</span>
          {trauma.sub && <> (<span style={{ color }}>{trauma.sub}</span>)</>}
        </div>
        {traumaReason && (
          <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '2px' }}>
            Reason: {traumaReason}
          </div>
        )}
      </div>
    </div>
  );
};

TraumaDisplay.propTypes = {
  traumaStatus: PropTypes.number,
  traumaReason: PropTypes.string,
};

TraumaDisplay.defaultProps = {
  traumaStatus: undefined,
  traumaReason: '',
};

const AddService = forwardRef((props, ref) => {
  const userObj = JSON.parse(localStorage.getItem('loginObj'));
  const roleCode = localStorage.getItem('userRole');

  const shouldShowSeparatePatientNameFields =
    props.mode === 'Edit' && (roleCode === 'GROUPPRADMIN' || roleCode === 'ACCADMIN');

  const {
    loading, rejectBill, serviceObj, serviceList, procedureList, docList,
    bill, viewReport, reportBlob,
    showPatientEdit, patientObj, parseDate,
    setPatientObj, setShowPatientEdit,
    getHospitalizationOptions,
    handleChange, saveService, handleApproval, handleReject,
    handleEditPatient, handlePatientUpdate,
    uploadBill, uploadReport, openUrl, deleteBlob, handleCancel,
  } = useAddService({ props, roleCode, userObj, shouldShowSeparatePatientNameFields });

  useImperativeHandle(ref, () => ({
    getValue: () => serviceObj,
    fromParent: () => setTimeout(() => saveService('B'), 0),
  }));

  const isLocked = serviceObj?.billingStatus === BILLING_LOCKED;
  const isApprovalDisabled =
    (roleCode === 'ACCADMIN' && [3, 4, 5, 6].includes(serviceObj?.billingStatus)) || props.tab === 1;
  const isUpdateDisabled = [4, 5].includes(serviceObj?.billingStatus);
  const isCalcDisabled =
    (roleCode === 'ACCADMIN' && [3, 4, 5].includes(serviceObj?.billingStatus)) || props.tab === 1;
  const isRejectDisabled =
    [3, 4, 5, 6].includes(serviceObj?.billingStatus) || props.tab === 1 || props.selected?.traumaStatus === 1;

  const approvalButtonLabel = () => {
    if (props.mode === 'Edit' && (roleCode === 'PRADMIN' || roleCode === 'GROUPPRADMIN')) return 'Get Finance Approval';
    if (props.mode === 'Edit' && roleCode === 'ACCADMIN') return 'Billing Completed';
    if (roleCode === 'PRADMIN' || roleCode === 'GROUPPRADMIN') return 'Get Finance Approval';
    return 'Get Admin Approval';
  };

  const patientEditFooter = (
    <div>
      <button className="bg-[#004172] text-white rounded-md px-4 py-2 mr-2" onClick={handlePatientUpdate}>
        Update
      </button>
      <button className="border border-[#004172] text-[#004172] rounded-md px-4 py-2" onClick={() => setShowPatientEdit(false)}>
        Cancel
      </button>
    </div>
  );

  return (
    <>
      <Backdrop sx={{ color: '#FFFFFF', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress size={80} color="inherit" />
      </Backdrop>

      <Dialog
        header="Edit Patient Name"
        visible={showPatientEdit}
        style={{ width: '50vw' }}
        footer={patientEditFooter}
        onHide={() => setShowPatientEdit(false)}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label htmlFor="patientFirstName">First Name</label>
            <InputText
              id="patientFirstName"
              value={patientObj.firstName || ''}
              onChange={(e) => setPatientObj((prev) => ({ ...prev, firstName: e.target.value }))}
              className="border border-gray-300 rounded-md p-2 w-full"
            />
          </div>
          <div>
            <label htmlFor="patientLastName">Last Name</label>
            <InputText
              id="patientLastName"
              value={patientObj.lastName || ''}
              onChange={(e) => setPatientObj((prev) => ({ ...prev, lastName: e.target.value }))}
              className="border border-gray-300 rounded-md p-2 w-full"
            />
          </div>
        </div>
      </Dialog>

      <div className="grid mt-4 grid-cols-3 gap-4">
        <div>
          <label htmlFor="patientName" className="flex items-center">
            Patient Name<span className="text-red-500">*</span>
            {shouldShowSeparatePatientNameFields && (
              <FaEdit className="ml-2 text-[#004172] cursor-pointer" onClick={handleEditPatient} />
            )}
          </label>
          <InputText
            id="patientName"
            name="patientName"
            disabled
            value={serviceObj?.patientName || ''}
            className="border border-gray-300 rounded-md p-2 w-full"
          />
        </div>

        <ReadonlyInput label="Hospital" value={serviceObj?.hospitalName} required />

        <SelectField
          id="paymentMode"
          name="paymentMode"
          label="Payment Mode"
          value={serviceObj?.paymentMode}
          onChange={handleChange}
          disabled={isLocked}
          options={serviceList}
          optionKey="code"
          optionLabel="name"
          required
        />

        <SelectField
          id="serviceType"
          name="serviceType"
          label="Service Type"
          value={serviceObj?.serviceType}
          onChange={handleChange}
          disabled={isLocked}
          options={SERVICE_TYPE_DS}
          optionKey="id"
          optionLabel="value"
          required
        />

        <SelectField
          id="serviceSubTypeId"
          name="serviceSubTypeId"
          label="Service Sub Type"
          value={serviceObj?.serviceSubTypeId}
          onChange={handleChange}
          disabled={isLocked}
          options={SUB_SERVICE_LIST}
          optionKey="value"
          optionLabel="name"
          required
        />

        <SelectField
          id="hospitalizationTypeId"
          name="hospitalizationTypeId"
          label="Hospitalization Type"
          value={serviceObj?.hospitalizationTypeId}
          onChange={handleChange}
          disabled={isLocked}
          options={getHospitalizationOptions()}
          optionKey="value"
          optionLabel="name"
          required
        />

        <SelectField
          id="procedureId"
          name="procedureId"
          label="Hospitalization Sub Type"
          value={serviceObj?.procedureId}
          onChange={(e) => { handleChange(e); }}
          disabled={isLocked}
          options={(procedureList || []).map((opt) => ({
            value: opt.hospitalizationSubTypeId,
            name: opt.hospitalizationSubType,
          }))}
          optionKey="value"
          optionLabel="name"
          required
        />

        {+serviceObj?.procedureId > 79 && +serviceObj?.procedureId < 95 &&
          (+serviceObj?.procedureId < 100 || +serviceObj?.procedureId > 106) && (
            <div>
              <label htmlFor="procedureNameDescription">Hospitalization Subtype Description</label>
              <InputText
                id="procedureNameDescription"
                name="procedureNameDescription"
                disabled={isLocked}
                onChange={handleChange}
                value={serviceObj?.procedureNameDescription || ''}
                className="border border-gray-300 rounded-md p-2 w-full"
              />
            </div>
          )}

        <NumberInput label="Gross Amount" name="gross" value={serviceObj?.gross} onChange={handleChange} disabled={isLocked} required />
        <NumberInput label={`MCI Amount${roleCode === 'CE' ? '' : ' *'}`} name="mci" value={serviceObj?.mci} onChange={handleChange} disabled={isLocked} />
        <ReadonlyInput label="Actual amount Billed" value={serviceObj?.amount} />

        <SelectField
          id="referral"
          name="referral"
          label="Referral"
          value={serviceObj?.referral}
          onChange={handleChange}
          disabled={isLocked}
          options={REFERRAL_LIST}
          optionKey="value"
          optionLabel="name"
          required={roleCode !== 'CE'}
        />

        {serviceObj?.referral == 1 && (
          <>
            <SelectField
              id="referralUserId"
              name="referralUserId"
              label="Doctors List"
              value={serviceObj?.referralUserId}
              onChange={handleChange}
              disabled={isLocked}
              options={(docList || []).map((d) => ({ value: d.DoctorCode, name: d.DoctorName }))}
              optionKey="value"
              optionLabel="name"
              required
            />
            <ReadonlyInput label="Doctor Share" value={serviceObj?.doctorShare} />
            <ReadonlyInput label="Doctor ShareType" value={serviceObj?.doctorShareType} />
          </>
        )}

        <ReadonlyInput label="Net Sale" value={serviceObj?.yieldAmount} />
        <ReadonlyInput label="MCI %" value={serviceObj?.mciPercentage} />
        <ReadonlyInput label="GMV %" value={serviceObj?.gmvPercentage} />
        <ReadonlyInput label="Net Sales %" value={serviceObj?.netSalesPercentage} />
        <ReadonlyInput label="Doctor Share %" value={serviceObj?.doctorSharePercentage} />

        <div>
          <label htmlFor="fromDate">Admission From Date<span className="text-red-500">*</span></label>
          <Calendar
            id="fromDate"
            name="fromDate"
            className="border border-gray-300 rounded-md p-2 w-full"
            disabled={isLocked}
            onChange={handleChange}
            value={parseDate(serviceObj?.fromDate)}
            dateFormat="dd/mm/yy"
            style={{ height: '40px' }}
          />
        </div>

        <div>
          <label htmlFor="toDate">Admission To Date<span className="text-red-500">*</span></label>
          <Calendar
            id="toDate"
            name="toDate"
            className="border border-gray-300 rounded-md p-2 w-full"
            disabled={isLocked}
            onChange={handleChange}
            value={parseDate(serviceObj?.toDate)}
            dateFormat="dd/mm/yy"
            style={{ height: '40px' }}
          />
        </div>

        <div>
          <label htmlFor="procedureDate">Billing Date<span className="text-red-500">*</span></label>
          <Calendar
            id="procedureDate"
            name="procedureDate"
            className="border border-gray-300 rounded-md p-2 w-full"
            disabled={isLocked}
            onChange={handleChange}
            value={serviceObj?.procedureDate ? parseDate(serviceObj.procedureDate) : new Date()}
            dateFormat="dd/mm/yy"
            style={{ height: '40px' }}
          />
        </div>

        <div>
          <label htmlFor="billingRemark">Billing Remark</label>
          <InputTextarea
            id="billingRemark"
            name="billingRemark"
            className="border border-gray-300 rounded-md p-2 w-full"
            disabled={isLocked || props.mode === 'View'}
            onChange={handleChange}
            value={serviceObj?.billingRemark || ''}
            rows={2}
            autoResize
            placeholder="Enter Billing Remark"
          />
        </div>

        {(rejectBill || props.selected?.reasonForReject) && (
          <div>
            <label htmlFor="reasonForReject">Reason For Reject<span className="text-red-500">*</span></label>
            <InputText
              id="reasonForReject"
              name="reasonForReject"
              onChange={handleChange}
              value={serviceObj?.reasonForReject || ''}
              className="border border-gray-300 rounded-md p-2 w-full"
            />
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 h-10 mt-5">
          {props.mode === 'Edit' && (
            <button
              className="bg-[#004172] text-white rounded-md w-full"
              disabled={isUpdateDisabled}
              style={{ opacity: isUpdateDisabled ? 0.5 : 1 }}
              onClick={() => saveService('U')}
            >
              Update
            </button>
          )}

          <button
            className="border border-[#004172] text-[#004172] font-bold rounded-md w-full"
            disabled={isCalcDisabled}
            style={{ opacity: isCalcDisabled ? 0.5 : 1 }}
            onClick={() => saveService(props.mode === 'Edit' ? 'C' : 'CS')}
          >
            {props.mode === 'Edit' ? 'Calculation' : 'Save & Calculate'}
          </button>

          {roleCode === 'ACCADMIN' && (
            <button
              className="bg-[#004172] text-white rounded-md w-full"
              disabled={isRejectDisabled}
              style={{ opacity: isRejectDisabled ? 0.5 : 1 }}
              onClick={handleReject}
            >
              Reject Billing
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mt-5">
        <div className="flex gap-3">
          <div className="mt-2">
            <label htmlFor="bill-upload" className="flex justify-items-center gap-2 text-[#004172] font-bold cursor-pointer">
              <FaUpload className="text-[#004172] mt-1" />
              Upload Bill
            </label>
            <input
              disabled={!serviceObj?.serviceEnquiryId}
              id="bill-upload"
              type="file"
              accept=".pdf, image/*"
              className="hidden w-full"
              onChange={uploadBill}
            />
          </div>
          {(serviceObj?.uploadBillName || bill) && (
            <button onClick={() => openUrl(serviceObj?.uploadBillName || bill)}>
              <FaEye className="text-[#6fb8ef] mt-3 mr-3" />
            </button>
          )}
        </div>

        <div>
          <div className="mt-2">
            <label htmlFor="report-upload" className="flex justify-items-center gap-2 text-[#004172] font-bold cursor-pointer">
              <FaUpload className="text-[#004172] mt-1" />
              Upload Report
            </label>
            <input
              disabled={!serviceObj?.serviceEnquiryId}
              id="report-upload"
              type="file"
              accept=".pdf, image/*"
              className="hidden"
              onChange={uploadReport}
              multiple
            />
          </div>
          {reportBlob.map((report) => (
            <div key={report.blobUrl || report.name} className="flex w-full">
              <a href={report.blobUrl} target="_blank" rel="noreferrer">{report.name}</a>
              <FaTrash className="ml-3 mt-2 cursor-pointer" onClick={() => deleteBlob(report.blobUrl)} />
            </div>
          ))}
        </div>

        <div className="mt-2">
          <span className="text-[#004172] font-bold">View Reports</span>
          <div className="flex w-full mt-3 ml-2">
            <div className="grid grid-cols-3">
              {viewReport.map((report, index) => (
                <button
                  key={report}
                  type="button"
                  onClick={() => openUrl(report)}
                  className="mr-3 cursor-pointer"
                  style={{ textDecoration: 'none', color: 'black', background: 'none', border: 'none', padding: 0 }}
                  onMouseOver={(e) => { e.target.style.textDecoration = 'underline'; }}
                  onFocus={(e) => { e.target.style.textDecoration = 'underline'; }}
                  onMouseOut={(e) => { e.target.style.textDecoration = 'none'; }}
                  onBlur={(e) => { e.target.style.textDecoration = 'none'; }}
                >
                  {`Rep-${index + 1}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <TraumaDisplay traumaStatus={props.selected?.traumaStatus} traumaReason={props.selected?.traumaReason} />

      <div className="grid mt-10 grid-cols-2 gap-4">
        <button
          className="h-9 bg-[#004172] text-white rounded-md w-full"
          disabled={isApprovalDisabled}
          style={{ opacity: isApprovalDisabled ? 0.5 : 1 }}
          onClick={handleApproval}
        >
          {approvalButtonLabel()}
        </button>
        <button className="h-9 border border-[#004172] text-[#004172] font-bold rounded-md w-full" onClick={handleCancel}>
          Cancel
        </button>
      </div>
    </>
  );
});

AddService.displayName = 'AddService';

AddService.propTypes = {
  mode: PropTypes.string,
  tab: PropTypes.number,
  selected: PropTypes.shape({
    traumaStatus: PropTypes.number,
    traumaReason: PropTypes.string,
    reasonForReject: PropTypes.string,
  }),
};

AddService.defaultProps = {
  mode: '',
  tab: undefined,
  selected: {},
};

export default AddService;